import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { storageService } from '../services/storageService';
import type { UserProfile } from '../types';
import { CameraIcon } from '../components/icons/PlaybackIcons';

const MobileProfilePage: React.FC = () => {
    const { currentUser, userProfile, refreshUserProfile, logout, resetPassword } = useAuth();
    const [formData, setFormData] = useState<Partial<UserProfile>>({
        firstName: '',
        lastName: '',
        teamName: '',
        heightFeet: 0,
        heightInches: 0,
        weightLbs: 0,
        age: 0,
        sex: '',
        usacLicenseNumber: '',
        ftp: 0,
        estimatedMatches: 0,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [resetPasswordStatus, setResetPasswordStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (userProfile) {
            setFormData({
                firstName: userProfile.firstName || '',
                lastName: userProfile.lastName || '',
                teamName: userProfile.teamName || '',
                heightFeet: userProfile.heightFeet || 0,
                heightInches: userProfile.heightInches || 0,
                weightLbs: userProfile.weightLbs || 0,
                bikeWeightLbs: userProfile.bikeWeightLbs || 0,
                age: userProfile.age || 0,
                sex: userProfile.sex || '',
                usacLicenseNumber: userProfile.usacLicenseNumber || '',
                ftp: userProfile.ftp || 0,
                estimatedMatches: userProfile.estimatedMatches || 0,
            });
        }
    }, [userProfile]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? parseFloat(value) : value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser) return;

        if (formData.bikeWeightLbs !== undefined && formData.bikeWeightLbs !== null && formData.bikeWeightLbs !== 0) {
            if (formData.bikeWeightLbs < 10 || formData.bikeWeightLbs > 40) {
                setMessage("Bike weight must be between 10 lbs and 40 lbs.");
                return;
            }
        }

        setIsSaving(true);
        setMessage(null);
        try {
            await userService.updateUserProfile(currentUser.uid, {
                ...userProfile, // preserve other fields
                ...formData,
            });
            if (formData.firstName && formData.lastName) {
                await currentUser.updateProfile({
                    displayName: `${formData.firstName} ${formData.lastName}`.trim()
                });
            }
            await refreshUserProfile();
            setMessage("Profile saved!");
            setTimeout(() => setMessage(null), 2000);
        } catch (error) {
            setMessage("Failed to save profile.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0] && currentUser) {
            const file = e.target.files[0];
            if (file.size > 5 * 1024 * 1024) {
                setMessage("Image must be less than 5MB.");
                return;
            }
            setIsUploading(true);
            try {
                const photoURL = await storageService.uploadProfilePicture(currentUser.uid, file);
                await currentUser.updateProfile({ photoURL });
                await userService.updateUserProfile(currentUser.uid, { photoURL });
                await refreshUserProfile();
            } catch (error) {
                setMessage("Failed to upload image.");
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleResetPassword = async () => {
        if (!currentUser?.email || !resetPassword) return;
        setResetPasswordStatus('sending');
        try {
            await resetPassword(currentUser.email);
            setResetPasswordStatus('success');
            setTimeout(() => setResetPasswordStatus('idle'), 5000);
        } catch (error) {
            console.error(error);
            setResetPasswordStatus('error');
            setTimeout(() => setResetPasswordStatus('idle'), 5000);
        }
    };

    return (
        <div className="p-6 max-w-md mx-auto animate-fade-in">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-white">Your Profile</h1>
                <button onClick={logout} className="text-sm text-gray-400 hover:text-red-400">Sign Out</button>
            </div>

            <div className="flex flex-col items-center mb-8">
                <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                    <img 
                        src={currentUser?.photoURL || `https://ui-avatars.com/api/?name=${currentUser?.displayName || 'User'}`} 
                        alt="Profile" 
                        className={`w-24 h-24 rounded-full border-4 border-gray-700 object-cover group-hover:opacity-75 transition-opacity ${isUploading ? 'opacity-50' : ''}`}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                        <CameraIcon className="w-8 h-8 text-white" />
                    </div>
                    {isUploading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        </div>
                    )}
                    <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/png, image/jpeg" className="hidden" />
                </div>
                <h2 className="text-xl font-bold text-white mt-4">{currentUser?.displayName}</h2>
                <p className="text-sm text-gray-400">{currentUser?.email}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {message && <div className="p-3 bg-cyan-900/50 text-cyan-200 border border-cyan-500 rounded-lg text-sm text-center">{message}</div>}
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">First Name</label>
                    <input type="text" name="firstName" value={formData.firstName || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Last Name</label>
                    <input type="text" name="lastName" value={formData.lastName || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Team Name</label>
                    <input type="text" name="teamName" value={formData.teamName || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">FTP (Watts)</label>
                    <input type="number" name="ftp" value={formData.ftp || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Height (Ft)</label>
                        <input type="number" name="heightFeet" value={formData.heightFeet || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Height (In)</label>
                        <input type="number" name="heightInches" value={formData.heightInches || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Body Weight (lbs)</label>
                        <input type="number" name="weightLbs" value={formData.weightLbs || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Bike Weight (lbs)</label>
                        <input type="number" name="bikeWeightLbs" placeholder="19" value={formData.bikeWeightLbs || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Age</label>
                    <input type="number" name="age" value={formData.age || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Sex</label>
                    <div className="relative">
                        <select name="sex" value={formData.sex || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base appearance-none">
                            <option value="" disabled>Select...</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Non-binary">Non-binary</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                        </div>
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">USAC License #</label>
                    <input type="text" name="usacLicenseNumber" value={formData.usacLicenseNumber || ''} onChange={handleChange} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-base" />
                </div>
                
                <div className="pt-6 border-t border-gray-800">
                    <h3 className="text-lg font-semibold text-cyan-400 mb-2">Matchbook Calibration</h3>
                    <p className="text-xs text-gray-400 mb-4">A 'match' represents a deep, anaerobic effort that dips significantly into your finite energy reserves. This is calculated dynamically based on your FTP and race history.</p>

                    {((userProfile?.racesAnalyzedForMatches || 0) < 5) && (
                        <div className="mb-4 bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                            <div className="flex justify-between text-xs text-gray-400 mb-1">
                                <span>Calibration Progress</span>
                                <span>{userProfile?.racesAnalyzedForMatches || 0} / 5 Races</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2">
                                <div className="bg-cyan-500 h-2 rounded-full transition-all" style={{ width: `${((userProfile?.racesAnalyzedForMatches || 0) / 5) * 100}%` }}></div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Upload more races to unlock a personalized matchbook. Defaulting to 4 matches.</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Capacity</label>
                        <div className="flex items-center h-[50px] bg-gray-800 border border-gray-700 rounded-xl px-4 text-xl overflow-hidden whitespace-nowrap">
                            {Array.from({ length: formData.estimatedMatches || (((userProfile?.racesAnalyzedForMatches || 0) < 5) ? 4 : 0) }).map((_, i) => (
                                <span key={i} title="1 Match">🔥</span>
                            ))}
                            {(!formData.estimatedMatches && ((userProfile?.racesAnalyzedForMatches || 0) >= 5)) && <span className="text-sm text-gray-500">Not set</span>}
                        </div>
                    </div>
                </div>
                
                <div className="pt-2 border-t border-gray-800 space-y-4">
                    <button
                        type="button"
                        onClick={handleResetPassword}
                        disabled={resetPasswordStatus === 'sending' || resetPasswordStatus === 'success'}
                        className={`w-full border font-bold py-3 rounded-xl transition-colors active:scale-95 disabled:opacity-80 disabled:cursor-not-allowed ${
                            resetPasswordStatus === 'success' ? 'bg-green-900/50 border-green-800 text-green-400' :
                            resetPasswordStatus === 'error' ? 'bg-red-900/50 border-red-800 text-red-400' :
                            'bg-gray-800 border-gray-700 hover:bg-gray-700 text-white'
                        }`}
                    >
                        {resetPasswordStatus === 'sending' ? 'Sending...' : resetPasswordStatus === 'success' ? 'Email sent! Check inbox.' : resetPasswordStatus === 'error' ? 'Failed to send' : 'Reset Password'}
                    </button>
                    <button type="submit" disabled={isSaving} className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 rounded-xl transition-colors active:scale-95">
                        {isSaving ? 'Saving...' : 'Save Profile'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default MobileProfilePage;